import type { BuildIntake } from './types';
import type { ConversationTurn } from './types';

export const BUILD_CONVERSATION_EVENT = 'selflabs:build-conversation';
export const BUILD_FILL_INTAKE_EVENT = 'selflabs:fill-build-intake';

export type BuildFillIntakeDetail = {
  intake: Partial<BuildIntake>;
  source?: 'milan' | 'analyze' | 'tool';
};

export function mergeIntakePatch(
  current: BuildIntake,
  patch: Partial<BuildIntake>
): BuildIntake {
  return {
    ...current,
    projectType: patch.projectType ?? current.projectType,
    scopeSize: patch.scopeSize ?? current.scopeSize,
    timeline: patch.timeline ?? current.timeline,
    projectName: patch.projectName?.trim() || current.projectName,
    company: patch.company?.trim() || current.company,
    contactName: patch.contactName?.trim() || current.contactName,
    email: patch.email?.trim() || current.email,
    description: patch.description?.trim() || current.description,
    conversationNotes: patch.conversationNotes?.trim() || current.conversationNotes,
    platforms: patch.platforms?.length ? patch.platforms : current.platforms,
    needsDesign: patch.needsDesign ?? current.needsDesign,
    needsAuth: patch.needsAuth ?? current.needsAuth,
    needsPayments: patch.needsPayments ?? current.needsPayments,
    needsAI: patch.needsAI ?? current.needsAI,
    needsAdminDashboard: patch.needsAdminDashboard ?? current.needsAdminDashboard,
    consultationHours: patch.consultationHours ?? current.consultationHours ?? 0,
    consultationMinutes: patch.consultationMinutes ?? current.consultationMinutes,
  };
}

export function dispatchBuildConversationTurn(turn: ConversationTurn): void {
  window.dispatchEvent(
    new CustomEvent(BUILD_CONVERSATION_EVENT, { detail: turn })
  );
}

export function broadcastFillBuildIntake(detail: BuildFillIntakeDetail): void {
  window.dispatchEvent(new CustomEvent(BUILD_FILL_INTAKE_EVENT, { detail }));

  document.querySelectorAll('iframe').forEach((frame) => {
    try {
      frame.contentWindow?.postMessage(
        { type: BUILD_FILL_INTAKE_EVENT, ...detail },
        window.location.origin
      );
    } catch {
      /* ignore cross-origin frames */
    }
  });
}

function extractTranscriptText(data: Record<string, unknown>): string | null {
  const candidates = [
    data.text,
    data.content,
    data.transcript,
    data.message,
    data.utterance,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return null;
}

function extractTranscriptRole(
  data: Record<string, unknown>,
  type: string
): 'user' | 'assistant' | null {
  const role = data.role ?? data.speaker ?? data.from;
  if (role === 'user' || role === 'client' || role === 'visitor') return 'user';
  if (role === 'assistant' || role === 'agent' || role === 'avatar' || role === 'milan') {
    return 'assistant';
  }
  if (type.includes('user')) return 'user';
  if (type.includes('assistant') || type.includes('agent')) return 'assistant';
  return null;
}

function handleManifestTranscriptMessage(event: MessageEvent): void {
  if (!event.origin.includes('manifestanything.ai') && !event.origin.includes('rolemodel.ai')) return;
  const data = event.data;
  if (!data || typeof data !== 'object') return;

  const record = data as Record<string, unknown>;
  const type = String(record.type ?? '').toLowerCase();

  if (!type.includes('transcript') && type !== 'manifest-avatar-message') return;

  const role = extractTranscriptRole(record, type);
  const text = extractTranscriptText(record);
  if (!role || !text) return;

  dispatchBuildConversationTurn({ role, content: text });
}

function patchFromToolArgs(args: Record<string, unknown>): Partial<BuildIntake> | null {
  const intake = args.intake ?? args.fields ?? args.form;
  if (intake && typeof intake === 'object') {
    return intake as Partial<BuildIntake>;
  }

  const patch: Partial<BuildIntake> = {};
  const keys: (keyof BuildIntake)[] = [
    'projectType',
    'projectName',
    'company',
    'contactName',
    'email',
    'description',
    'scopeSize',
    'timeline',
    'platforms',
    'needsDesign',
    'needsAuth',
    'needsPayments',
    'needsAI',
    'needsAdminDashboard',
    'consultationHours',
    'conversationNotes',
  ];

  let found = false;
  for (const key of keys) {
    if (args[key] !== undefined) {
      (patch as Record<string, unknown>)[key] = args[key];
      found = true;
    }
  }
  return found ? patch : null;
}

const BRIDGE_WRAPPED = '__buildIntakeBridgeWrapped';

export function installBuildIntakeBridge(): () => void {
  const onMessage = (e: MessageEvent) => handleManifestTranscriptMessage(e);
  window.addEventListener('message', onMessage);

  const original = window.__manifestRunAvatarTool;
  if (original && !(original as Record<string, unknown>)[BRIDGE_WRAPPED]) {
    const wrapped = (tool: string, args?: Record<string, unknown>) => {
      const name = (tool || '').toLowerCase();
      const payload = args && typeof args === 'object' ? args : {};

      if (name === 'fill_build_intake' || name === 'update_build_intake') {
        const patch = patchFromToolArgs(payload);
        if (patch) {
          broadcastFillBuildIntake({ intake: patch, source: 'tool' });
          return { ok: true, filled: true };
        }
        return { ok: false, error: 'No intake fields provided' };
      }

      return original(tool, payload);
    };
    (wrapped as Record<string, unknown>)[BRIDGE_WRAPPED] = true;
    window.__manifestRunAvatarTool = wrapped;
  }

  return () => {
    window.removeEventListener('message', onMessage);
  };
}
